import type { FC, ReactNode } from 'react'

import { Button } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import classNames from 'classnames'

import { Table } from 'common/components/Table/Table'

import type { SeriesTemplate } from '../../api/series.types'
import { StatusPill } from '../shared/StatusPill'
import { cadenceText } from '../shared/cadenceText'
import { SERIES_PAGES_TEXT } from '../shared/seriesPagesText'
import styles from './seriesTemplatesPage.module.scss'
import type { TemplateBusy, TemplateBusyAction } from './useTemplateActions'

const TEXT = SERIES_PAGES_TEXT.templates

// Board TGRID widths plus the 10px gap, edge cells plus the 18px inset; Series and Facility fill.
const WIDTHS = [undefined, 60, 114, 98, 146, undefined, 114, 138, 106, 237] as const

type TemplateHandlers = {
  busy: TemplateBusy | null
  onEdit: (template: SeriesTemplate) => void
  onDuplicate: (template: SeriesTemplate) => void
  onToggleActive: (template: SeriesTemplate) => void
}

type SeriesTemplatesTableProps = TemplateHandlers & { rows: SeriesTemplate[] }

const SeriesCell: FC<{ template: SeriesTemplate }> = ({ template }) => (
  <div className={styles.stack}>
    <span className={styles.strong}>{template.name}</span>
    <span className={styles.sub}>{template.description ?? TEXT.noValue}</span>
  </div>
)

const InUseCell: FC<{ template: SeriesTemplate }> = ({ template }) => (
  <div className={styles.stack}>
    <span className={styles.strong}>{template.inUse}</span>
    <span className={styles.sub}>{TEXT.inUse(template.inUse)}</span>
  </div>
)

const billingText = (template: SeriesTemplate): string =>
  template.billingCode ? `${template.billingCode.type}:${template.billingCode.code}` : TEXT.noValue

const text = (value: ReactNode) => <span className={styles.text}>{value}</span>

const ActionsCell: FC<TemplateHandlers & { template: SeriesTemplate }> = ({
  template,
  busy,
  onEdit,
  onDuplicate,
  onToggleActive,
}) => {
  const isBusy = busy?.id === template.id
  const spins = (action: TemplateBusyAction) => isBusy && busy?.action === action
  return (
    <div className={styles.actions}>
      <Button
        size='small'
        className={classNames(styles.small, styles.primary)}
        disabled={isBusy}
        onClick={() => onEdit(template)}>
        {TEXT.edit}
      </Button>
      <Button
        size='small'
        className={styles.small}
        loading={spins('duplicate')}
        disabled={isBusy && !spins('duplicate')}
        onClick={() => onDuplicate(template)}>
        {TEXT.duplicate}
      </Button>
      <Button
        size='small'
        className={styles.small}
        loading={spins('toggle')}
        disabled={isBusy && !spins('toggle')}
        onClick={() => onToggleActive(template)}>
        {template.active ? TEXT.retire : TEXT.restore}
      </Button>
    </div>
  )
}

const buildColumns = (
  props: Omit<SeriesTemplatesTableProps, 'rows'>,
): ColumnsType<SeriesTemplate> => {
  const cells: ((template: SeriesTemplate) => ReactNode)[] = [
    (t) => <SeriesCell template={t} />,
    (t) => text(t.visits),
    (t) => text(t.cadenceLabel ?? cadenceText(t.every, t.unit)),
    (t) => text(TEXT.visitLength(t.duration)),
    (t) => text(t.category?.name ?? TEXT.noValue),
    (t) => text(t.facility?.name ?? TEXT.anyFacility),
    (t) => <span className={styles.code}>{billingText(t)}</span>,
    (t) => <InUseCell template={t} />,
    (t) => (
      <StatusPill tone={t.active ? 'active' : 'muted'}>
        {t.active ? TEXT.pillAvailable : TEXT.pillRetired}
      </StatusPill>
    ),
    (t) => <ActionsCell template={t} {...props} />,
  ]
  return TEXT.columns.map((title, index) => ({
    key: title,
    title,
    width: WIDTHS[index],
    render: (_: unknown, template: SeriesTemplate) => cells[index](template),
  }))
}

export const SeriesTemplatesTable: FC<SeriesTemplatesTableProps> = ({ rows, ...handlers }) => (
  <Table
    className={styles.table}
    itemKey='id'
    columns={buildColumns(handlers)}
    dataSource={rows}
    isLoading={false}
    haveActions={false}
    pagination={false}
    rowClassName={(template: SeriesTemplate) => (template.active ? '' : styles.mutedRow)}
  />
)
